import classnames from 'classnames';
import { BatchFormFieldType } from 'components/batchForm/BatchForm.helpers';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useBatchPastedDataContext } from 'components/common-form/contexts/batch/BatchPasteDataContext';
import { useData } from 'components/common-form/hooks/useData';
import { useSPIntl } from 'hooks/useIntl';
import { Underlying } from 'models/underlying';
import { FC, useContext, useMemo } from 'react';

import { BatchFormNavLayerProps } from './BatchFormNavLayer.models';
import { StyledFormCellWrapper } from './BatchFormNavLayer.styles';
import { DragHandle } from './DragHandle';
import { useCellInteractions } from './hooks/useCellInteractions';
import { useCellState } from './hooks/useCellState';
import { useKeyboardHandling } from './hooks/useKeyboardHandling';
import { usePasteData } from './hooks/usePasteData';

export const BatchFormNavLayer: FC<BatchFormNavLayerProps> = ({
  id,
  rowNameTranslationKey,
  formId,
  className,
  rowIndex,
  columnIndex,
  children,
  type,
  canEdit,
  setLostInputBeforeEditing,
  metadata,
  formStatus,
  selectFormCallback,
}) => {
  const { formatMessage } = useSPIntl();
  const [value, setValue] = useData<BatchFormFieldType | undefined>(id);
  const [underlyings] = useData<ReadonlyArray<Underlying>>('Underlyings');

  const navigationContext = useContext(BatchNavigationContext);
  const batchPasteDataContext = useBatchPastedDataContext();

  const { isSelected, isEditing, isEditingCell } = useCellState(navigationContext);

  const cellInteractions = useCellInteractions({
    rowIndex,
    columnIndex,
    value,
    setValue,
    type,
    metadata,
    formStatus,
    canEdit,
    setLostInputBeforeEditing,
    navigationContext,
    batchPasteDataContext,
  });

  const keyboardHandling = useKeyboardHandling({
    formId,
    type,
    value,
    setValue,
    metadata,
    canEdit,
    underlyings,
    selectFormCallback,
    setLostInputBeforeEditing,
    navigationContext,
    batchPasteDataContext,
    cellInteractions,
  });

  usePasteData({
    metadata,
    value,
    setValue,
    cellId: cellInteractions.cellId,
    batchPasteDataContext,
    formatMessage,
  });

  const cellClassName = useMemo(
    () =>
      classnames(
        className,
        'focus-ring position-relative',
        { 'bg-light': isSelected },
        { 'bg-light bg-opacity-40': cellInteractions.isDragHoveringCell },
      ),
    [className, isSelected, cellInteractions.isDragHoveringCell],
  );

  const rowNameLabel = useMemo(
    () => formatMessage({ id: rowNameTranslationKey as any, defaultMessage: rowNameTranslationKey }),
    [formatMessage, rowNameTranslationKey],
  );

  return (
    <StyledFormCellWrapper
      data-e2e={`${id}-cell-${columnIndex}`}
      role="gridcell"
      aria-label={rowNameLabel}
      aria-colindex={columnIndex + 1}
      aria-rowindex={rowIndex + 1}
      key={id}
      className={cellClassName}
      onClick={cellInteractions.onClick}
      onDoubleClick={cellInteractions.onDoubleClick}
      onKeyDown={isEditing ? undefined : keyboardHandling.onKeyDown}
      onDragOver={cellInteractions.handleDragOver}
      onDrop={cellInteractions.handleDrop}
      tabIndex={-1}
      onBlur={cellInteractions.onBlur}
      onMouseEnter={cellInteractions.onMouseEnter}
      onMouseLeave={cellInteractions.onMouseLeave}
      ref={cellInteractions.gridCellRef}
      $canEdit={canEdit}
      $isEditing={isEditingCell ?? false}
    >
      {children}
      <DragHandle
        isDisplayed={cellInteractions.isDragHandledDisplayed}
        onDragStart={cellInteractions.handleDragStart}
      />
    </StyledFormCellWrapper>
  );
};

BatchFormNavLayer.displayName = 'BatchFormNavLayer';
